import chroma from "chroma-js";

/**
 * Ten colours, taken off a picture.
 * ---------------------------------------------------------------------------
 * Someone has a screenshot of a product, a photograph of a building, a brand
 * sheet - and wants the cards to be those colours. The useful part of that is
 * not "the average colour of this image", which is always mud: it is the few
 * colours the picture is actually MADE of, which is a counting problem.
 *
 * So: the image is drawn small (a hundred pixels across is plenty - this is
 * about what colours are there, not where), every pixel is rounded into a
 * coarse bucket, and the buckets are counted. The busiest ones win.
 *
 * Three rules keep the result usable as a palette rather than faithful as a
 * sample:
 *
 *   - near-black and near-white are dropped. Almost every photograph is mostly
 *     one or the other, and a palette of ten greys is not what anyone is
 *     asking for by picking a picture;
 *   - the colourless are dropped before the colourful, but only while there
 *     are enough colourful ones to fill the palette - a genuinely grey picture
 *     still gets to be a grey palette;
 *   - what is picked is spread apart: two buckets a shade from each other are
 *     the same colour as far as the eye is concerned, and ten of those is one
 *     swatch repeated.
 *
 * Then it is sorted light to dark, because that is what the cards expect of a
 * palette: the tint a card takes is an index, and a ramp makes a shelf read as
 * one set rather than as confetti.
 * ---------------------------------------------------------------------------
 */

/* How big the picture is read at. Small on purpose: counting a million pixels
   says nothing a hundred thousand do not. */
const SAMPLE = 120;

/* The bucket, in bits per channel. Five is 32 levels a channel - fine enough
   to keep two blues apart, coarse enough that a gradient does not become a
   thousand buckets of one pixel each. */
const BITS = 5;
const SHIFT = 8 - BITS;

/* How far apart two colours have to be before they count as two. */
const APART = 14;

function bitmapFrom(file) {
  if (typeof createImageBitmap === "function") return createImageBitmap(file);
  /* an older browser: the long way round, through an <img> */
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("that file is not a picture this browser can read")); };
    img.src = url;
  });
}

/** Every bucket in the picture, busiest first. */
function census(pixels) {
  const seen = new Map();
  for (let i = 0; i < pixels.length; i += 4) {
    /* anything see-through is not a colour of the picture */
    if (pixels[i + 3] < 200) continue;
    const r = pixels[i] >> SHIFT;
    const g = pixels[i + 1] >> SHIFT;
    const b = pixels[i + 2] >> SHIFT;
    const key = (r << (BITS * 2)) | (g << BITS) | b;
    const was = seen.get(key);
    if (was) { was.n += 1; was.r += pixels[i]; was.g += pixels[i + 1]; was.b += pixels[i + 2]; }
    else seen.set(key, { n: 1, r: pixels[i], g: pixels[i + 1], b: pixels[i + 2] });
  }

  return [...seen.values()]
    .map(c => ({
      n: c.n,
      /* the bucket's own average, so the colour is one that is really in the
         picture rather than the corner of the box it fell in */
      color: chroma(Math.round(c.r / c.n), Math.round(c.g / c.n), Math.round(c.b / c.n)),
    }))
    .sort((a, b) => b.n - a.n);
}

const lum = (c) => c.luminance();
const sat = (c) => c.get("hsl.s") || 0;

/**
 * @param file   what someone dropped in
 * @param count  how many swatches to come back with
 * @returns      an array of hex strings, light to dark
 */
export async function paletteFromImage(file, count = 10) {
  const bitmap = await bitmapFrom(file);
  const w = bitmap.width || SAMPLE;
  const h = bitmap.height || SAMPLE;
  const scale = Math.min(1, SAMPLE / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0, cw, ch);
  if (typeof bitmap.close === "function") bitmap.close();

  const all = census(ctx.getImageData(0, 0, cw, ch).data);
  if (!all.length) throw new Error("there is nothing in that picture to take a colour from");

  /* the ends of the range are the picture's paper and ink, not its colours */
  const middle = all.filter(c => lum(c.color) > 0.03 && lum(c.color) < 0.92);
  const colourful = middle.filter(c => sat(c.color) > 0.12);
  const pool = colourful.length >= count ? colourful : (middle.length ? middle : all);

  /* busiest first, but never two of the same colour */
  const picked = [];
  pool.forEach(c => {
    if (picked.length >= count) return;
    if (picked.some(p => chroma.distance(p, c.color) < APART)) return;
    picked.push(c.color);
  });

  /* a picture with only three colours in it still owes us ten: the gaps are
     filled by walking the ones it has */
  if (!picked.length) picked.push(chroma("#f6400d"));
  while (picked.length < count) {
    const a = picked[(picked.length - 1) % picked.length];
    const b = picked[picked.length % picked.length === 0 ? 0 : (picked.length - 2 + picked.length) % picked.length];
    picked.push(chroma.mix(a, b || a, 0.5, "lab").darken(0.2));
  }

  return picked
    .slice(0, count)
    .sort((a, b) => lum(b) - lum(a))
    .map(c => c.hex());
}

/**
 * The same palette with the colour taken out.
 *
 * Luminance rather than an average of the channels: grey that keeps how LIGHT
 * each swatch was is still a ramp, and a ramp is what a shelf of cards reads
 * as one set by.
 */
export function greyscale(colors) {
  return (colors || []).map(c => {
    try {
      const l = chroma(c).luminance();
      /* back out of linear luminance, so a mid colour lands on a mid grey
         rather than on something much darker */
      const v = Math.round(255 * Math.pow(Math.max(0, Math.min(1, l)), 1 / 2.2));
      return chroma(v, v, v).hex();
    } catch (e) { return "#808080"; }
  });
}

export default paletteFromImage;
